import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Bell, Plus, Loader2, Settings, Trash2, Pencil, Play, Pause, 
  Mail, Smartphone, Clock, Calendar, ChevronRight, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, Zap, ArrowUpRight, Sparkles, PlusCircle,
  User, UserCog, Users, Shield, UserCheck, UsersRound, Eye, Copy
} from "lucide-react";

const DIAS_SEMANA = [
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mié" },
  { value: 4, label: "Jue" },
  { value: 5, label: "Vie" },
  { value: 6, label: "Sáb" },
  { value: 7, label: "Dom" }
];

// Operadores de comparación para filtros
const OPERADORES_COMPARACION = [
  { value: "igual_a", label: "Igual a" },
  { value: "diferente_de", label: "Diferente de" },
  { value: "contiene", label: "Contiene" },
  { value: "no_contiene", label: "No contiene" },
  { value: "comienza_con", label: "Comienza con" },
  { value: "termina_con", label: "Termina con" },
  { value: "mayor_que", label: "Mayor que" },
  { value: "menor_que", label: "Menor que" },
  { value: "mayor_o_igual", label: "Mayor o igual" },
  { value: "menor_o_igual", label: "Menor o igual" },
  { value: "esta_vacio", label: "Está vacío" },
  { value: "no_esta_vacio", label: "No está vacío" }
];

export default function ReglasAlertasPage() {
  const [reglas, setReglas] = useState([]);
  const [config, setConfig] = useState({ modulos: {}, tipos_accion: [], condiciones: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingRegla, setViewingRegla] = useState(null);
  const [editingRegla, setEditingRegla] = useState(null);
  const [filterModulo, setFilterModulo] = useState("all");
  
  // New event creation state
  const [showNewEventSection, setShowNewEventSection] = useState(false);
  const [newEventForm, setNewEventForm] = useState({ 
    value: "", 
    label: "", 
    condiciones: [],
    destinatarios_predeterminados: []  // quién recibirá la alerta por defecto
  });
  const [savingEvent, setSavingEvent] = useState(false);
  const [camposModulo, setCamposModulo] = useState([]);  // Campos disponibles para el módulo seleccionado
  
  // Opciones de destinatarios predeterminados
  const DESTINATARIOS_OPCIONES = [
    { id: "creador", label: "Usuario que creó el registro", icon: "User" },
    { id: "usuario_asignado", label: "Usuario asignado", icon: "UserCheck" },
    { id: "supervisor_usuario", label: "Supervisor del usuario", icon: "UserCog" },
    { id: "supervisor", label: "Supervisor del área", icon: "UserCog" },
    { id: "departamento", label: "Todo el departamento", icon: "Users" },
    { id: "administrador", label: "Administrador del sistema", icon: "Shield" },
    { id: "responsable_modulo", label: "Responsable del módulo", icon: "UserCheck" },
    { id: "todos", label: "Todos los usuarios activos", icon: "UsersRound" }
  ];
  
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    modulo: "",
    evento: "",
    filtro_evento: { campo: "", operador: "igual_a", valor: "" },  // Filtro para el evento
    condiciones: {},
    acciones: [],
    escalamiento: { activo: false, tiempo_espera_minutos: 60, nivel: 1, destinatarios: [] },
    horario_inicio: "08:00",
    horario_fin: "18:00",
    dias_semana: [1, 2, 3, 4, 5],
    activo: true
  });
  
  const [accionForm, setAccionForm] = useState({ 
    tipo: "email", 
    destinatarios_logicos: [],  // IDs de destinatarios lógicos
    destinatarios_emails: "",   // Emails manuales
    plantilla_mensaje: "" 
  });
  const [escalaDestinatario, setEscalaDestinatario] = useState("");
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [reglasRes, configRes] = await Promise.all([
        axios.get(`${API}/reglas-alertas`, getAuthHeaders()),
        axios.get(`${API}/reglas-alertas/config`, getAuthHeaders())
      ]);
      setReglas(reglasRes.data.items || []);
      setConfig(configRes.data || { modulos: {}, tipos_accion: [], condiciones: {} });
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({
      nombre: "", descripcion: "", modulo: "", evento: "", 
      filtro_evento: { campo: "", operador: "igual_a", valor: "" },
      condiciones: {},
      acciones: [],
      escalamiento: { activo: false, tiempo_espera_minutos: 60, nivel: 1, destinatarios: [] },
      horario_inicio: "08:00", horario_fin: "18:00", dias_semana: [1, 2, 3, 4, 5], activo: true
    });
    setAccionForm({ tipo: "email", destinatarios_logicos: [], destinatarios_emails: "", plantilla_mensaje: "" });
    setEditingRegla(null);
    setShowNewEventSection(false);
    setNewEventForm({ value: "", label: "", condiciones: [], destinatarios_predeterminados: [] });
  };

  const handleEdit = (regla) => {
    setEditingRegla(regla);
    setFormData({
      nombre: regla.nombre,
      descripcion: regla.descripcion || "",
      modulo: regla.modulo,
      evento: regla.evento,
      filtro_evento: regla.filtro_evento || { campo: "", operador: "igual_a", valor: "" },
      condiciones: regla.condiciones || {},
      acciones: regla.acciones || [],
      escalamiento: regla.escalamiento || { activo: false, tiempo_espera_minutos: 60, nivel: 1, destinatarios: [] },
      horario_inicio: regla.horario_inicio || "08:00",
      horario_fin: regla.horario_fin || "18:00",
      dias_semana: regla.dias_semana || [1, 2, 3, 4, 5],
      activo: regla.activo
    });
    loadCamposModulo(regla.modulo);
    setShowDialog(true);
  };

  const handleView = (regla) => {
    setViewingRegla(regla);
    setShowViewDialog(true);
  };

  const handleClone = (regla) => {
    setEditingRegla(null);
    setFormData({
      nombre: `${regla.nombre} (Copia)`,
      descripcion: regla.descripcion || "",
      modulo: regla.modulo,
      evento: regla.evento,
      filtro_evento: regla.filtro_evento || { campo: "", operador: "igual_a", valor: "" },
      condiciones: regla.condiciones || {},
      acciones: regla.acciones || [],
      escalamiento: regla.escalamiento || { activo: false, tiempo_espera_minutos: 60, nivel: 1, destinatarios: [] },
      horario_inicio: regla.horario_inicio || "08:00",
      horario_fin: regla.horario_fin || "18:00",
      dias_semana: regla.dias_semana || [1, 2, 3, 4, 5],
      activo: false  // Cloned rules start inactive
    });
    loadCamposModulo(regla.modulo);
    setShowDialog(true);
    toast.info("Editando copia de la regla. Modifique el nombre y guarde.");
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.modulo || !formData.evento) {
      toast.error("Complete los campos requeridos");
      return;
    }
    if (formData.acciones.length === 0) {
      toast.error("Agregue al menos una acción");
      return;
    }
    
    setSaving(true);
    try {
      if (editingRegla) {
        await axios.put(`${API}/reglas-alertas/${editingRegla.id}`, formData, getAuthHeaders());
        toast.success("Regla actualizada");
      } else {
        await axios.post(`${API}/reglas-alertas`, formData, getAuthHeaders());
        toast.success("Regla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar esta regla?")) return;
    try {
      await axios.delete(`${API}/reglas-alertas/${id}`, getAuthHeaders());
      toast.success("Regla eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.put(`${API}/reglas-alertas/${id}/toggle`, {}, getAuthHeaders());
      toast.success(res.data.activo ? "Regla activada" : "Regla desactivada");
      fetchData();
    } catch (err) {
      toast.error("Error al cambiar estado");
    }
  };

  const addAccion = () => {
    if (!accionForm.tipo) return;
    
    // Combine logical recipients and manual emails
    const emailsManual = accionForm.destinatarios_emails.split(",").map(d => d.trim()).filter(d => d);
    const logicos = accionForm.destinatarios_logicos || [];
    
    // Convert logical IDs to descriptive labels for display
    const logicosLabels = logicos.map(id => {
      const opt = DESTINATARIOS_OPCIONES.find(o => o.id === id);
      return opt ? `[${opt.label}]` : id;
    });
    
    const allDestinatarios = [...logicosLabels, ...emailsManual];
    
    if (allDestinatarios.length === 0) {
      toast.error("Seleccione al menos un destinatario");
      return;
    }
    
    setFormData({
      ...formData,
      acciones: [...formData.acciones, { 
        tipo: accionForm.tipo, 
        destinatarios: allDestinatarios,
        destinatarios_logicos: logicos,
        destinatarios_emails: emailsManual,
        plantilla_mensaje: accionForm.plantilla_mensaje 
      }]
    });
    setAccionForm({ tipo: "email", destinatarios_logicos: [], destinatarios_emails: "", plantilla_mensaje: "" });
  };

  const toggleAccionDestinatario = (destId) => {
    const current = accionForm.destinatarios_logicos || [];
    if (current.includes(destId)) {
      setAccionForm({ ...accionForm, destinatarios_logicos: current.filter(d => d !== destId) });
    } else {
      setAccionForm({ ...accionForm, destinatarios_logicos: [...current, destId] });
    }
  };

  const removeAccion = (index) => {
    setFormData({ ...formData, acciones: formData.acciones.filter((_, i) => i !== index) });
  };

  const addEscalaDestinatario = () => {
    if (!escalaDestinatario.trim()) return;
    setFormData({
      ...formData,
      escalamiento: {
        ...formData.escalamiento,
        destinatarios: [...(formData.escalamiento.destinatarios || []), escalaDestinatario.trim()]
      }
    });
    setEscalaDestinatario("");
  };

  const getEventosModulo = () => {
    if (!formData.modulo || !config.modulos[formData.modulo]) return [];
    return config.modulos[formData.modulo].eventos || [];
  };

  const getCondicionesEvento = () => {
    const evento = getEventosModulo().find(e => e.value === formData.evento);
    if (!evento) return [];
    return evento.condiciones.map(c => ({ key: c, ...config.condiciones[c] }));
  };

  // Load campos when module changes
  const loadCamposModulo = async (modulo) => {
    if (!modulo) {
      setCamposModulo([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/reglas-alertas/campos/${modulo}`, getAuthHeaders());
      setCamposModulo(res.data.campos || []);
    } catch (err) {
      console.error("Error loading campos:", err);
      // Use generic campos if API fails
      setCamposModulo([
        { id: "id", label: "ID del Registro" },
        { id: "created_at", label: "Fecha de Creación" },
        { id: "estado", label: "Estado" }
      ]);
    }
  };

  // Handle module change
  const handleModuloChange = (modulo) => {
    setFormData({...formData, modulo: modulo, evento: "", condiciones: {}});
    setShowNewEventSection(false);
    setNewEventForm({ value: "", label: "", condiciones: [], destinatarios_predeterminados: [] });
    loadCamposModulo(modulo);
  };

  // Handle campo selection for new event
  const handleCampoSelect = (campoId) => {
    const campo = camposModulo.find(c => c.id === campoId);
    if (campo) {
      setNewEventForm({
        ...newEventForm, 
        value: campoId,
        label: campo.label
      });
    }
  };

  // Create new custom event
  const handleCreateEvent = async () => {
    if (!formData.modulo) {
      toast.error("Primero seleccione un módulo");
      return;
    }
    if (!newEventForm.value || !newEventForm.label) {
      toast.error("Complete el identificador y nombre del evento");
      return;
    }
    
    // Validate value format (lowercase, no spaces, underscores allowed)
    const valueRegex = /^[a-z0-9_]+$/;
    if (!valueRegex.test(newEventForm.value)) {
      toast.error("El identificador debe ser en minúsculas, sin espacios (use guiones bajos)");
      return;
    }
    
    setSavingEvent(true);
    try {
      await axios.post(`${API}/reglas-alertas/eventos`, {
        modulo: formData.modulo,
        value: newEventForm.value,
        label: newEventForm.label,
        condiciones: newEventForm.condiciones,
        destinatarios_predeterminados: newEventForm.destinatarios_predeterminados
      }, getAuthHeaders());
      
      toast.success("Evento creado exitosamente");
      
      // Refresh config to get new events
      const configRes = await axios.get(`${API}/reglas-alertas/config`, getAuthHeaders());
      setConfig(configRes.data);
      
      // Select the new event
      setFormData({ ...formData, evento: newEventForm.value });
      
      // Reset new event form
      setNewEventForm({ value: "", label: "", condiciones: [], destinatarios_predeterminados: [] });
      setShowNewEventSection(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear evento");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteCustomEvent = async (eventoId) => {
    if (!confirm("¿Eliminar este evento personalizado?")) return;
    try {
      await axios.delete(`${API}/reglas-alertas/eventos/${eventoId}`, getAuthHeaders());
      toast.success("Evento eliminado");
      
      // Refresh config
      const configRes = await axios.get(`${API}/reglas-alertas/config`, getAuthHeaders());
      setConfig(configRes.data);
    } catch (err) {
      toast.error("Error al eliminar evento");
    }
  };

  const toggleCondicion = (condKey) => {
    const current = newEventForm.condiciones || [];
    if (current.includes(condKey)) {
      setNewEventForm({ ...newEventForm, condiciones: current.filter(c => c !== condKey) });
    } else {
      setNewEventForm({ ...newEventForm, condiciones: [...current, condKey] });
    }
  };

  const toggleDestinatario = (destId) => {
    const current = newEventForm.destinatarios_predeterminados || [];
    if (current.includes(destId)) {
      setNewEventForm({ ...newEventForm, destinatarios_predeterminados: current.filter(d => d !== destId) });
    } else {
      setNewEventForm({ ...newEventForm, destinatarios_predeterminados: [...current, destId] });
    }
  };

  const getDestinatarioIcon = (iconName) => {
    const icons = {
      "User": <User className="w-4 h-4" />,
      "UserCog": <UserCog className="w-4 h-4" />,
      "Users": <Users className="w-4 h-4" />,
      "Shield": <Shield className="w-4 h-4" />,
      "UserCheck": <UserCheck className="w-4 h-4" />,
      "UsersRound": <UsersRound className="w-4 h-4" />
    };
    return icons[iconName] || <User className="w-4 h-4" />;
  };

  const filteredReglas = filterModulo === "all" ? reglas : reglas.filter(r => r.modulo === filterModulo);

  const getAccionIcon = (tipo) => {
    switch (tipo) {
      case "email": return <Mail className="w-4 h-4" />;
      case "notificacion": return <Bell className="w-4 h-4" />;
      case "push": return <Smartphone className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Zap className="w-7 h-7 text-amber-500" />Reglas de Alertas
              </h1>
              <p className="text-slate-500 mt-1">Configura alertas automáticas para toda la plataforma</p>
            </div>
            <div className="flex gap-2">
              <Select value={filterModulo} onValueChange={setFilterModulo}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Filtrar por módulo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los módulos</SelectItem>
                  {Object.entries(config.modulos).map(([key, mod]) => (
                    <SelectItem key={key} value={key}>{mod.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-[#0C314C] text-white">
                  <Plus className="w-4 h-4 mr-2" />Nueva Regla
                </Button>
              )}
            </div>
          </div>

          {/* Rules List */}
          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : filteredReglas.length === 0 ? (
            <Card className="p-12 text-center">
              <Bell className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay reglas configuradas</p>
              {isAdmin && (
                <Button onClick={() => setShowDialog(true)} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />Crear primera regla
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredReglas.map(regla => (
                <Card key={regla.id} className={`transition-all ${!regla.activo ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{regla.nombre}</h3>
                          <Badge variant={regla.activo ? "default" : "secondary"}>
                            {regla.activo ? "Activa" : "Inactiva"}
                          </Badge>
                          <Badge variant="outline">{config.modulos[regla.modulo]?.label || regla.modulo}</Badge>
                        </div>
                        {regla.descripcion && <p className="text-sm text-slate-500 mb-2">{regla.descripcion}</p>}
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {regla.horario_inicio} - {regla.horario_fin}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {regla.dias_semana?.map(d => DIAS_SEMANA.find(ds => ds.value === d)?.label).join(", ")}
                          </span>
                          <span className="flex items-center gap-2">
                            {regla.acciones?.map((a, i) => (
                              <span key={i} className="flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded">
                                {getAccionIcon(a.tipo)}
                                {a.tipo}
                              </span>
                            ))}
                          </span>
                        </div>
                        {regla.escalamiento?.activo && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-orange-600">
                            <ArrowUpRight className="w-4 h-4" />
                            Escalamiento activo: {regla.escalamiento.tiempo_espera_minutos} min
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleView(regla)} title="Ver detalles">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(regla.id)} title={regla.activo ? "Desactivar" : "Activar"}>
                          {regla.activo ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(regla)} title="Editar">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleClone(regla)} title="Clonar">
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(regla.id)} title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
            <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b">
              <DialogTitle>{editingRegla ? "Editar Regla" : "Nueva Regla de Alerta"}</DialogTitle>
              <DialogDescription>Configure las condiciones y acciones para esta alerta</DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto px-6" style={{maxHeight: "calc(85vh - 200px)"}}>
              <div className="space-y-4 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nombre de la Regla *</Label>
                    <Input value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} placeholder="Ej: Alerta de facturas por vencer" />
                  </div>
                  <div className="col-span-2">
                    <Label>Descripción</Label>
                    <Textarea value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} placeholder="Descripción opcional..." rows={2} />
                  </div>
                </div>

                {/* Module & Event */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Módulo *</Label>
                    <Select value={formData.modulo} onValueChange={handleModuloChange}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar módulo" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(config.modulos).map(([key, mod]) => (
                          <SelectItem key={key} value={key}>{mod.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Evento Disparador *</Label>
                    <Select value={formData.evento} onValueChange={v => setFormData({...formData, evento: v})} disabled={!formData.modulo}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar evento" /></SelectTrigger>
                      <SelectContent>
                        {getEventosModulo().map(ev => (
                          <SelectItem key={ev.value} value={ev.value}>
                            <div className="flex items-center gap-2">
                              {ev.label}
                              {ev.personalizado && <Badge variant="secondary" className="text-xs ml-1">Personalizado</Badge>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Filtro de Condición del Evento */}
                {formData.evento && camposModulo.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Label className="text-amber-800 mb-3 block flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Condición de Disparo (opcional)
                    </Label>
                    <p className="text-sm text-amber-600 mb-3">
                      Especifique cuándo exactamente debe dispararse la alerta. Por ejemplo: "Estado igual a Pendiente"
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm">Campo a evaluar</Label>
                        <Select 
                          value={formData.filtro_evento?.campo || "ninguno"} 
                          onValueChange={v => setFormData({
                            ...formData, 
                            filtro_evento: { ...formData.filtro_evento, campo: v === "ninguno" ? "" : v }
                          })}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Seleccionar campo..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ninguno">Sin filtro</SelectItem>
                            {camposModulo.map(campo => (
                              <SelectItem key={campo.id} value={campo.id}>
                                {campo.label || campo.id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Operador</Label>
                        <Select 
                          value={formData.filtro_evento?.operador || "igual_a"} 
                          onValueChange={v => setFormData({
                            ...formData, 
                            filtro_evento: { ...formData.filtro_evento, operador: v }
                          })}
                          disabled={!formData.filtro_evento?.campo}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OPERADORES_COMPARACION.map(op => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Valor</Label>
                        <Input
                          placeholder="Ej: Pendiente, Aprobado..."
                          value={formData.filtro_evento?.valor || ""}
                          onChange={e => setFormData({
                            ...formData, 
                            filtro_evento: { ...formData.filtro_evento, valor: e.target.value }
                          })}
                          disabled={!formData.filtro_evento?.campo || ["esta_vacio", "no_esta_vacio"].includes(formData.filtro_evento?.operador)}
                          className="bg-white"
                        />
                      </div>
                    </div>
                    {formData.filtro_evento?.campo && formData.filtro_evento?.valor && (
                      <div className="mt-3 p-2 bg-white rounded border border-amber-300">
                        <p className="text-sm text-amber-700">
                          <strong>Resumen:</strong> Esta regla se disparará cuando el campo "
                          <span className="font-semibold">{camposModulo.find(c => c.id === formData.filtro_evento.campo)?.label || formData.filtro_evento.campo}</span>" 
                          sea <span className="font-semibold">{OPERADORES_COMPARACION.find(o => o.value === formData.filtro_evento.operador)?.label?.toLowerCase()}</span> "
                          <span className="font-semibold">{formData.filtro_evento.valor}</span>"
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Create New Event Section */}
                {formData.modulo && isAdmin && (
                  <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-violet-800 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Crear Evento Personalizado
                      </Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowNewEventSection(!showNewEventSection)}
                        className="text-violet-600"
                      >
                        {showNewEventSection ? "Cancelar" : <><PlusCircle className="w-4 h-4 mr-1" />Nuevo Evento</>}
                      </Button>
                    </div>
                    
                    {showNewEventSection && (
                      <div className="space-y-3 mt-3 pt-3 border-t border-violet-200">
                        <p className="text-sm text-violet-600">
                          ¿No encuentras el evento que necesitas? Crea uno personalizado para el módulo "{config.modulos[formData.modulo]?.label}".
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Identificador único *</Label>
                            <Select value={newEventForm.value} onValueChange={handleCampoSelect}>
                              <SelectTrigger className="bg-white">
                                <SelectValue placeholder="Seleccionar campo..." />
                              </SelectTrigger>
                              <SelectContent>
                                {camposModulo.map(campo => (
                                  <SelectItem key={campo.id} value={campo.id}>
                                    {campo.id} - {campo.label}
                                  </SelectItem>
                                ))}
                                <SelectItem value="custom">+ Crear identificador personalizado</SelectItem>
                              </SelectContent>
                            </Select>
                            {newEventForm.value === "custom" && (
                              <Input 
                                placeholder="ej: nuevo_ticket" 
                                value={newEventForm.customValue || ""}
                                onChange={e => setNewEventForm({
                                  ...newEventForm, 
                                  customValue: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                                  value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                                })}
                                className="bg-white mt-2"
                              />
                            )}
                            <p className="text-xs text-slate-400 mt-1">Seleccione de la fuente de datos o cree uno personalizado</p>
                          </div>
                          <div>
                            <Label className="text-sm">Nombre visible *</Label>
                            <Input 
                              placeholder="ej: Nuevo ticket creado" 
                              value={newEventForm.label}
                              onChange={e => setNewEventForm({...newEventForm, label: e.target.value})}
                              className="bg-white"
                            />
                            <p className="text-xs text-slate-400 mt-1">Este nombre aparecerá en las alertas</p>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-sm mb-2 block">Condiciones aplicables (opcional)</Label>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(config.condiciones || {}).map(([key, cond]) => (
                              <label key={key} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded border cursor-pointer hover:bg-violet-50">
                                <Checkbox 
                                  checked={newEventForm.condiciones?.includes(key)}
                                  onCheckedChange={() => toggleCondicion(key)}
                                />
                                <span className="text-sm">{cond.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm mb-2 block">¿A quién notificar? (opcional)</Label>
                          <p className="text-xs text-violet-500 mb-2">Seleccione los destinatarios predeterminados para este evento</p>
                          <div className="grid grid-cols-2 gap-2">
                            {DESTINATARIOS_OPCIONES.map(dest => (
                              <label 
                                key={dest.id} 
                                className={`flex items-center gap-2 bg-white px-3 py-2 rounded border cursor-pointer transition-colors
                                  ${newEventForm.destinatarios_predeterminados?.includes(dest.id) 
                                    ? 'border-violet-500 bg-violet-50' 
                                    : 'hover:bg-violet-50'}`}
                              >
                                <Checkbox 
                                  checked={newEventForm.destinatarios_predeterminados?.includes(dest.id)}
                                  onCheckedChange={() => toggleDestinatario(dest.id)}
                                />
                                <span className="text-violet-600">{getDestinatarioIcon(dest.icon)}</span>
                                <span className="text-sm">{dest.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        <Button 
                          type="button" 
                          onClick={handleCreateEvent} 
                          disabled={savingEvent}
                          className="bg-violet-600 hover:bg-violet-700 text-white"
                        >
                          {savingEvent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                          Crear Evento
                        </Button>
                      </div>
                    )}
                    
                    {/* List custom events for current module */}
                    {!showNewEventSection && getEventosModulo().filter(e => e.personalizado).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-violet-200">
                        <p className="text-xs text-violet-600 mb-2">Eventos personalizados en este módulo:</p>
                        <div className="flex flex-wrap gap-2">
                          {getEventosModulo().filter(e => e.personalizado).map(ev => (
                            <Badge key={ev.value} variant="secondary" className="flex items-center gap-1 bg-white">
                              {ev.label}
                              <button 
                                onClick={() => handleDeleteCustomEvent(ev.id)} 
                                className="ml-1 hover:text-red-500"
                                title="Eliminar evento"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Conditions */}
                {getCondicionesEvento().length > 0 && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <Label className="text-blue-800 mb-3 block">Condiciones</Label>
                    <div className="grid grid-cols-2 gap-4">
                      {getCondicionesEvento().map(cond => (
                        <div key={cond.key}>
                          <Label className="text-sm">{cond.label}</Label>
                          <Input
                            type="number"
                            value={formData.condiciones[cond.key] || cond.default}
                            onChange={e => setFormData({...formData, condiciones: {...formData.condiciones, [cond.key]: parseInt(e.target.value) || 0}})}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <Label className="text-green-800 mb-3 block">Acciones *</Label>
                  
                  {/* Action Type Selection */}
                  <div className="mb-4">
                    <Label className="text-sm mb-2 block">Tipo de Acción</Label>
                    <Select value={accionForm.tipo} onValueChange={v => setAccionForm({...accionForm, tipo: v})}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {config.tipos_accion?.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Logical Recipients */}
                  <div className="mb-4">
                    <Label className="text-sm mb-2 block">¿A quién enviar? (seleccione uno o más)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {DESTINATARIOS_OPCIONES.map(dest => (
                        <label 
                          key={dest.id}
                          className={`flex items-center gap-2 bg-white px-3 py-2 rounded border cursor-pointer transition-colors text-sm
                            ${accionForm.destinatarios_logicos?.includes(dest.id) 
                              ? 'border-green-500 bg-green-50' 
                              : 'hover:bg-green-50'}`}
                        >
                          <Checkbox 
                            checked={accionForm.destinatarios_logicos?.includes(dest.id)}
                            onCheckedChange={() => toggleAccionDestinatario(dest.id)}
                          />
                          <span className="text-green-700">{getDestinatarioIcon(dest.icon)}</span>
                          <span>{dest.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Manual Emails */}
                  <div className="mb-4">
                    <Label className="text-sm mb-2 block">Emails adicionales (opcional, separados por coma)</Label>
                    <Input 
                      placeholder="email1@empresa.com, email2@empresa.com" 
                      value={accionForm.destinatarios_emails} 
                      onChange={e => setAccionForm({...accionForm, destinatarios_emails: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                  
                  <Button type="button" variant="outline" onClick={addAccion} className="w-full border-green-300 text-green-700 hover:bg-green-100">
                    <Plus className="w-4 h-4 mr-2" />Agregar Acción
                  </Button>
                  
                  {/* Added Actions List */}
                  {formData.acciones.length > 0 && (
                    <div className="space-y-2 mt-4">
                      <Label className="text-sm text-green-700">Acciones configuradas:</Label>
                      {formData.acciones.map((acc, i) => (
                        <div key={i} className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex-1">
                            <span className="flex items-center gap-2 font-medium">
                              {getAccionIcon(acc.tipo)}
                              <span className="capitalize">{acc.tipo}</span>
                            </span>
                            <p className="text-sm text-slate-500 mt-1">
                              Destinatarios: {acc.destinatarios?.join(", ") || "Sistema"}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeAccion(i)}>
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Escalation */}
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-orange-800">Escalamiento</Label>
                    <Switch checked={formData.escalamiento?.activo} onCheckedChange={v => setFormData({...formData, escalamiento: {...formData.escalamiento, activo: v}})} />
                  </div>
                  {formData.escalamiento?.activo && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Tiempo de espera (minutos)</Label>
                          <Input type="number" value={formData.escalamiento.tiempo_espera_minutos} onChange={e => setFormData({...formData, escalamiento: {...formData.escalamiento, tiempo_espera_minutos: parseInt(e.target.value) || 60}})} />
                        </div>
                        <div>
                          <Label className="text-sm">Nivel de escalamiento</Label>
                          <Input type="number" min={1} max={5} value={formData.escalamiento.nivel} onChange={e => setFormData({...formData, escalamiento: {...formData.escalamiento, nivel: parseInt(e.target.value) || 1}})} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Destinatarios del escalamiento</Label>
                        <div className="flex gap-2">
                          <Input placeholder="Email del escalamiento" value={escalaDestinatario} onChange={e => setEscalaDestinatario(e.target.value)} />
                          <Button type="button" variant="outline" size="sm" onClick={addEscalaDestinatario}><Plus className="w-4 h-4" /></Button>
                        </div>
                        {formData.escalamiento.destinatarios?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {formData.escalamiento.destinatarios.map((d, i) => (
                              <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({...formData, escalamiento: {...formData.escalamiento, destinatarios: formData.escalamiento.destinatarios.filter((_, idx) => idx !== i)}})}>{d} ×</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Schedule */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <Label className="text-purple-800 mb-3 block">Horario de Aplicación</Label>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <Label className="text-sm">Hora inicio</Label>
                      <Input type="time" value={formData.horario_inicio} onChange={e => setFormData({...formData, horario_inicio: e.target.value})} />
                    </div>
                    <div>
                      <Label className="text-sm">Hora fin</Label>
                      <Input type="time" value={formData.horario_fin} onChange={e => setFormData({...formData, horario_fin: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Días de la semana</Label>
                    <div className="flex gap-2">
                      {DIAS_SEMANA.map(dia => (
                        <Button
                          key={dia.value}
                          type="button"
                          variant={formData.dias_semana?.includes(dia.value) ? "default" : "outline"}
                          size="sm"
                          className={formData.dias_semana?.includes(dia.value) ? "bg-purple-600" : ""}
                          onClick={() => {
                            const dias = formData.dias_semana || [];
                            setFormData({...formData, dias_semana: dias.includes(dia.value) ? dias.filter(d => d !== dia.value) : [...dias, dia.value]});
                          }}
                        >
                          {dia.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Active */}
                <div className="flex items-center justify-between p-4 bg-slate-100 rounded-lg">
                  <div>
                    <Label>Regla Activa</Label>
                    <p className="text-sm text-slate-500">La regla se ejecutará automáticamente</p>
                  </div>
                  <Switch checked={formData.activo} onCheckedChange={v => setFormData({...formData, activo: v})} />
                </div>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 border-t p-6 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0C314C] text-white">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingRegla ? "Actualizar" : "Crear Regla"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                Detalles de la Regla
              </DialogTitle>
            </DialogHeader>
            
            {viewingRegla && (
              <div className="space-y-4 py-4">
                {/* Header Info */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{viewingRegla.nombre}</h3>
                  <Badge className={viewingRegla.activo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                    {viewingRegla.activo ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
                
                {viewingRegla.descripcion && (
                  <p className="text-slate-600">{viewingRegla.descripcion}</p>
                )}
                
                {/* Module & Event */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-slate-500 text-sm">Módulo</Label>
                    <p className="font-medium">{config.modulos[viewingRegla.modulo]?.label || viewingRegla.modulo}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">Evento Disparador</Label>
                    <p className="font-medium">{viewingRegla.evento}</p>
                  </div>
                </div>
                
                {/* Filtro de Evento */}
                {viewingRegla.filtro_evento?.campo && viewingRegla.filtro_evento?.valor && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Label className="text-amber-800 mb-2 block flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Condición de Disparo
                    </Label>
                    <p className="text-sm text-amber-700">
                      Se dispara cuando el campo "<strong>{viewingRegla.filtro_evento.campo}</strong>" 
                      es <strong>{OPERADORES_COMPARACION.find(o => o.value === viewingRegla.filtro_evento.operador)?.label?.toLowerCase() || viewingRegla.filtro_evento.operador}</strong> "
                      <strong>{viewingRegla.filtro_evento.valor}</strong>"
                    </p>
                  </div>
                )}
                
                {/* Actions */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <Label className="text-blue-800 mb-2 block">Acciones ({viewingRegla.acciones?.length || 0})</Label>
                  <div className="space-y-2">
                    {viewingRegla.acciones?.map((accion, i) => (
                      <div key={i} className="p-3 bg-white rounded border">
                        <div className="flex items-center gap-2 mb-2">
                          {accion.tipo === "email" ? <Mail className="w-4 h-4 text-blue-600" /> : <Smartphone className="w-4 h-4 text-green-600" />}
                          <span className="font-medium capitalize">{accion.tipo}</span>
                        </div>
                        {accion.destinatarios_logicos?.length > 0 && (
                          <div className="text-sm text-slate-600">
                            <span className="font-medium">Destinatarios: </span>
                            {accion.destinatarios_logicos.map(d => 
                              DESTINATARIOS_OPCIONES.find(opt => opt.id === d)?.label || d
                            ).join(", ")}
                          </div>
                        )}
                        {accion.destinatarios_emails?.length > 0 && (
                          <div className="text-sm text-slate-600">
                            <span className="font-medium">Emails adicionales: </span>
                            {accion.destinatarios_emails.join(", ")}
                          </div>
                        )}
                        {accion.plantilla_mensaje && (
                          <div className="text-sm text-slate-500 mt-2 italic">
                            "{accion.plantilla_mensaje}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Schedule */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <Label className="text-purple-800 mb-2 block">Horario</Label>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {viewingRegla.horario_inicio || viewingRegla.horario?.hora_inicio || "00:00"} - {viewingRegla.horario_fin || viewingRegla.horario?.hora_fin || "23:59"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {(viewingRegla.dias_semana || viewingRegla.horario?.dias_semana || [1,2,3,4,5]).map(d => 
                        DIAS_SEMANA.find(ds => ds.value === d)?.label
                      ).join(", ")}
                    </span>
                  </div>
                </div>
                
                {/* Escalation */}
                {viewingRegla.escalamiento?.activo && (
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <Label className="text-orange-800 mb-2 block flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4" />
                      Escalamiento Activo
                    </Label>
                    <p className="text-sm text-slate-600">
                      Espera {viewingRegla.escalamiento.tiempo_espera_minutos} minutos antes de escalar
                    </p>
                    {viewingRegla.escalamiento.destinatarios?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {viewingRegla.escalamiento.destinatarios.map((d, i) => (
                          <Badge key={i} variant="secondary">{d}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Metadata */}
                <div className="text-xs text-slate-400 pt-2 border-t">
                  <p>ID: {viewingRegla.id}</p>
                  <p>Creado: {new Date(viewingRegla.created_at).toLocaleString()}</p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => handleEdit(viewingRegla)}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </Button>
              <Button variant="outline" onClick={() => handleClone(viewingRegla)}>
                <Copy className="w-4 h-4 mr-2" />
                Clonar
              </Button>
              <Button onClick={() => setShowViewDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
